import React, { useState, forwardRef } from 'react';
import { motion } from 'framer-motion';

// Vibrant solid colors
const colors = [
  '#00FF9F', // UI Green
  '#FF1493', // Deep pink
  '#0066FF', // Bright blue
  '#FF6B00', // Orange
  '#FF00FF', // Hot pink
  '#FFB800', // Amber
  '#FF4D00', // Bright orange
  '#FF8A00'  // Golden orange
];

// Font style variations - only using VT323
const fontStyles = [
  'font-["VT323"] tracking-tighter',
  'font-["VT323"] tracking-normal',
  'font-["VT323"] tracking-wide',
  'font-["VT323"] tracking-wider',
  'font-["VT323"] tracking-widest'
];

const getRandomStates = () => {
  const getRandomColor = (excludeColor) => {
    let availableColors = colors.filter(color => color !== excludeColor);
    return availableColors[Math.floor(Math.random() * availableColors.length)];
  };

  // Generate colors ensuring no adjacent duplicates
  const letterColors = [];
  for (let i = 0; i < 5; i++) {
    letterColors.push(getRandomColor(letterColors[i - 1]));
  }

  return {
    colors: letterColors,
    styles: Array(5).fill(0).map(() => Math.floor(Math.random() * fontStyles.length)),
    cases: ['R', 'E', 'T', 'R', 'O'].map(letter => 
      Math.random() > 0.5 ? letter.toLowerCase() : letter.toUpperCase()
    ),
    states: Array(5).fill(0).map(() => ({
      scale: 0.6 + Math.random() * 1,
      rotation: Math.random() * 16 - 8,
      y: (Math.random() * 12 - 6)
    }))
  };
};

const RetroWordmark = forwardRef(({ size = 'md', className = '', alignment = 'left' }, ref) => {
  const initialStates = getRandomStates();
  const [letterColors, setLetterColors] = useState(initialStates.colors);
  const [letterStyles, setLetterStyles] = useState(initialStates.styles);
  const [letterCases, setLetterCases] = useState(initialStates.cases);
  const [letterStates, setLetterStates] = useState(initialStates.states);

  const handleClick = () => {
    const newStates = getRandomStates();
    setLetterColors(newStates.colors);
    setLetterStyles(newStates.styles);
    setLetterCases(newStates.cases);
    setLetterStates(newStates.states);

    window.dispatchEvent(new Event('retrowordmark-click'));
  };

  // Size variants - expanded range
  const sizeClasses = {
    xs: 'text-[18px]',
    sm: 'text-[24px]',
    md: 'text-[32px]',
    lg: 'text-[48px]',
    xl: 'text-[64px]',
    '2xl': 'text-[96px]',
    '3xl': 'text-[128px]',
    '4xl': 'text-[164px]'
  };

  const alignmentClasses = {
    left: '',
    center: 'mx-auto',
    right: 'ml-auto'
  };

  return (
    <motion.div
      ref={ref}
      className={`select-none py-2 relative ${alignmentClasses[alignment]} ${className}`}
      onClick={handleClick}
      transition={{ type: "spring", stiffness: 400, damping: 10 }}
    >
      <div className="flex items-center gap-[2px]">
        {letterCases.map((letter, i) => (
          <motion.div
            key={`${letter}-${i}-${letterStyles[i]}`}
            animate={{
              scale: letterStates[i].scale,
              rotate: letterStates[i].rotation,
              y: letterStates[i].y
            }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 15,
              mass: 0.5 + Math.random() * 0.3,
              velocity: 5
            }}
            className={`${sizeClasses[size]} ${fontStyles[letterStyles[i]]}`}
            style={{
              color: letterColors[i],
              display: 'inline-block',
              transformOrigin: 'center center'
            }}
          >
            {letter}
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
});

RetroWordmark.displayName = 'RetroWordmark';

export default RetroWordmark; 